import React, { useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import Filter from '../../Filter';
import Table from '../../Table';
import Pagination from '../../TablePagination';
import LimitButton from '../../LimitSelectBox';
import StaffCreate from './StaffCreate';
import StaffUpdate from './StaffUpdate';
import StaffDelete from './StaffDelete';
import StaffkeyIssue from '../staffkeys/StaffkeyIssue';
import { dateFormatter, staffRoleFormatter, staffStatusFormatter } from '../../../lib/formatter';

const StaffList = ({
  staffListTotal,
  staffListItems,
  filterItem,
  paginationItem,
  currentPageNumber,
  listStaffApi,
  setFilterItem,
  setPaginationItem,
  setCurrentPageNumber,
  history,
}) => {
  const { t } = useTranslation();
  const [ isOpenStaffCreateModal, setIsOpenStaffCreateModal ] = useState(false);
  const [ isOpenStaffUpdateModal, setIsOpenStaffUpdateModal ] = useState(false);
  const [ isOpenStaffkeyIssueModal, setIsOpenStaffkeyIssueModal ] = useState(false);
  const [ isOpenStaffDeleteModal, setIsOpenStaffDeleteModal ] = useState(false);
  const [ selectedStaffId, setSelectedStaffId ] = useState('');
  const [ selectedStaffName, setSelectedStaffName ] = useState('');

  const roleItems = [
    { key: t('data.staff_role.master'), value: 'master' },
    { key: t('data.staff_role.manager'), value: 'manager' },
    { key: t('data.staff_role.housekeeping'), value: 'housekeeping' },
    { key: t('data.staff_role.manager_mobilekey'), value: 'manager_mobilekey' },
    { key: t('data.staff_role.doorlock_setting'), value: 'doorlock_setting' },
  ];

  const statusItems = [
    { key: t('data.regist_status.granting'), value: 'granting' },
    { key: t('data.regist_status.granted'), value: 'granted' },
  ];

  const changePagination = (pageNumber) => {
    setCurrentPageNumber(pageNumber);
    setPaginationItem({
      ...paginationItem,
      offset: (pageNumber - 1) * paginationItem.limit,
    });
  };

  const changeLimit = (limit) => {
    setCurrentPageNumber(1);
    setPaginationItem({
      offset: 0,
      limit,
    });
  };

  //디바운싱 사용 구분을 위해 debounce props 추가
  const changeFilterItem = (targetItem, debounce) => {
    setFilterItem({
      ...filterItem,
      ...targetItem,
    }, debounce);
    setCurrentPageNumber(1);
    setPaginationItem({
      ...paginationItem,
      offset: 0,
    });
  };

  const initFilterItem = () => {
    setFilterItem({
      name: '',
      role: '',
      status: '',
    });
    setCurrentPageNumber(1);
    setPaginationItem({
      ...paginationItem,
      offset: 0,
    });
  };

  const selectStaffUpdate = (staffId) => {
    setSelectedStaffId(staffId);
    setIsOpenStaffUpdateModal(true);
  };

  const selectStaffkeyIssue = (staffId, role) => {
    if (role === 'master') return;
    setSelectedStaffId(staffId);
    setIsOpenStaffkeyIssueModal(true);
  };

  const selectStaffDelete = (staffId, staffName) => {
    setSelectedStaffId(staffId);
    setSelectedStaffName(staffName)
    setIsOpenStaffDeleteModal(true);
  };

  return (
    <Fragment>
      <div className='content-title2'>
        <h3>{t('content_title.staff_list')}</h3>
      </div>
      <div className='content-title'>
        <Filter
          columns={[
            {
              key: 'name',
              size: 'lg',
              placeholder: t('data_column.staff_name'),
              value: filterItem.name,
              action: changeFilterItem,
            },
            {
              key: 'role',
              type: 'select',
              size: 'xlg',
              placeholder: t('data_column.role'),
              value: filterItem.role,
              action: changeFilterItem,
              selectItems: roleItems,
              selectItemKey: 'key',
              selectItemValue: 'value',
              isRemoveButton: true,
            },
            {
              key: 'status',
              type: 'select',
              size: 'xlg',
              placeholder: t('data_column.regist_status'),
              value: filterItem.status,
              action: changeFilterItem,
              selectItems: statusItems,
              selectItemKey: 'key',
              selectItemValue: 'value',
              isRemoveButton: true,
            },
          ]}
          init={initFilterItem}
        />
      </div>
      <Table
        columns={[
          {
            key: 'view',
            text: t('data_column.view'),
            width: '8%',
            moveToPage: {
              func: (staffId) => history.push(`/staff/${staffId}`),
              params: ['id'],
            },
          },
          {
            key: 'name',
            text: t('data_column.staff_name'),
            width: '17%',
          },
          {
            key: 'phone_number',
            text: t('data_column.phone_number'),
            width: '15%',
          },
          {
            key: 'role',
            text: t('data_column.role'),
            width: '12%',
            formatter: {
              func: staffRoleFormatter,
              params: ['role'],
            },
          },
          {
            key: 'status',
            text: t('data_column.regist_status'),
            width: '13%',
            formatter: {
              func: staffStatusFormatter,
              params: ['status'],
            },
          },
          {
            key: 'cardkey_count',
            text: t('data_column.cardkey_count'),
            width: '10%',
          },
          {
            key: 'mobilekey_count',
            text: t('data_column.mobilekey_count'),
            width: '10%',
          },
          {
            key: 'created_at',
            text: t('data_column.created_at'),
            width: '15%',
            formatter: {
              func: dateFormatter,
              params: ['created_at'],
            },
          },
          {
            key: 'button',
            text: '',
            width: '5%',
            items: [
              {
                text: t('button.edit'),
                action: {
                  func: selectStaffUpdate,
                  params: ['id'],
                },
              },
              {
                text: t('button.key_issue'),
                disabled: {
                  func: (role) => role === 'master',
                  params: ['role'],
                },
                action: {
                  func: selectStaffkeyIssue,
                  params: ['id', 'role'],
                },
              },
              {
                text: t('button.delete'),
                action: {
                  func: selectStaffDelete,
                  params: ['id', 'name'],
                },
              },
            ],
          },
        ]}
        datas={staffListItems}
      />
      <LimitButton
        currentLimit={paginationItem.limit}
        changeLimit={changeLimit}
      />
      <div className='form-item-add'>
        <button className='btn-item-add list' onClick={() => setIsOpenStaffCreateModal(true)}>
          <FontAwesomeIcon icon={faPlusCircle} className='icon-item-add'/>
          <span>{t('button.staff_add')}</span>
        </button>
      </div>
      <Pagination
        total={staffListTotal}
        index={currentPageNumber}
        limit={paginationItem.limit}
        indexChange={changePagination}
      />
      <StaffCreate
        isOpen={isOpenStaffCreateModal}
        toggle={() => setIsOpenStaffCreateModal(!isOpenStaffCreateModal)}
        reload={() => listStaffApi()}
      />
      <StaffUpdate
        isOpen={isOpenStaffUpdateModal}
        toggle={() => setIsOpenStaffUpdateModal(!isOpenStaffUpdateModal)}
        reload={() => listStaffApi()}
        staffId={selectedStaffId}
      />
      <StaffkeyIssue
        isOpen={isOpenStaffkeyIssueModal}
        toggle={() => setIsOpenStaffkeyIssueModal(!isOpenStaffkeyIssueModal)}
        reload={() => listStaffApi()}
        staffId={selectedStaffId}
      />
      <StaffDelete
        isOpen={isOpenStaffDeleteModal}
        toggle={() => setIsOpenStaffDeleteModal(!isOpenStaffDeleteModal)}
        reload={() => listStaffApi()}
        staffId={selectedStaffId}
        staffName={selectedStaffName}
      />
    </Fragment>
  );
};

export default StaffList;